/* eslint-disable @emotion/syntax-preference -- FIXME */
import React from 'react';

import { css } from '@emotion/react';
import {
  DropdownMenu,
  Heading,
  HStack,
  Primitive,
  Text,
  VStack,
} from '@rtkwlf/fenrir-react';
import type { AttachmentFile, TicketWithComments } from '@rtkwlf/rendall';
import { TicketStatus } from '@rtkwlf/rendall';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useRendallApi } from '@apis/rendall/use-rendall-api';
import { LogoBadge } from '@features/tickets/components/logo-badge/logo-badge';
import { TicketType } from '@features/tickets/components/ticket/ticket-type';
import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import { TicketStatusBadge } from '@packages/components/ticket-status-badge/ticket-status-badge';
import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';
import { DateTime } from 'ae-components/dateTime/DateTime';

import { TicketAction } from '../ticketAction/TicketAction';

import { DetailsTextRow } from './detailsTextRow/DetailsTextRow';
import { TicketAttachments } from './ticketComments/ticketAttachments/TicketAttachments';
import { TicketComments } from './ticketComments/TicketComments';
import { DetailsValue } from './ticketDescription/DetailsValue';
import { FollowUpButton } from './ticketDetailsReadingPane/FollowUpButton';
import {
  csvType,
  TicketEvidenceNavigator,
} from './ticketEvidenceNavigator/TicketEvidenceNavigator';

type DetailsBadgeRowProps = { value: string; titleKey?: string };

type AttachmentsProps = { attachments?: Array<AttachmentFile> };

export const EMPTY_TICKET: Required<TicketWithComments> = {
  attachments: [],
  canReply: true,
  canSolve: true,
  cc: [],
  ccAgent: [],
  comments: [],
  createdAt: '2021-11-16T15:35:07Z',
  description: '',
  id: 0,
  requester: '',
  requesterAgent: false,
  status: TicketStatus.New,
  subject: '',
  type: '',
  updatedAt: '2021-11-21T15:03:48Z',
};

export const DetailsBadgeRow: React.FC<DetailsBadgeRowProps> = ({
  value,
  titleKey,
}) => {
  const { t } = useTranslation();

  return (
    <VStack
      css={css({
        rowGap: '0.55rem',
        width: 'fit-content',
      })}
    >
      {titleKey ? <Text styledAs="body4">{t(`${titleKey}`)}</Text> : null}
      <TicketStatusBadge value={value} />
    </VStack>
  );
};

const Attachments: React.FC<AttachmentsProps> = ({ attachments }) => {
  const { t } = useTranslation();

  return (
    <VStack
      css={css({
        width: 'fit-content',
      })}
    >
      <Text styledAs="body4">
        {t('ticket:details.attachments')}
        {attachments?.length && (
          <Primitive.span marginLeft="xsmall">{`(${attachments.length})`}</Primitive.span>
        )}
      </Text>
      {attachments?.length ? (
        <TicketAttachments attachments={attachments} />
      ) : (
        <DetailsTextRow value={t('ticket:details.noData')} />
      )}
    </VStack>
  );
};

type TicketDetailsProps = { ticketId: string };

export const TicketDetails: React.FC<TicketDetailsProps> = ({ ticketId }) => {
  const { t } = useTranslation();

  const organization = useSelectedOrganization();
  const api = useRendallApi();

  const {
    isPending,
    isError,
    data: ticket = EMPTY_TICKET,
  } = useQuery<TicketWithComments>({
    queryFn: () => {
      const params = {
        organizationId: organization.id,
        ticketId,
      };

      return api.getTicketV2(params);
    },
    queryKey: ticketKeys.detail(organization.id, ticketId),
  });

  return (
    <GeneralWrapper isError={isError} isLoading={isPending}>
      <HStack
        alignItems="flex-start"
        css={css`
          @media screen and (max-width: 1480px) {
            flex-direction: column;
          }
        `}
      >
        <VStack
          padding="large"
          yAlign="top"
          width="75%"
          css={css`
            @media screen and (max-width: 1480px) {
              order: 2;
            }
          `}
        >
          <Heading level="2">{ticket.subject}</Heading>
          <Heading level="4" marginTop="medium">
            {t('ticket:details.ticketType')}
          </Heading>
          <TicketType value={ticket.type} />
          <Heading level="4" marginTop="medium">
            {t('ticket:details.description')}
          </Heading>
          <DetailsValue value={ticket.description ?? ''} type="markdown" />

          <TicketEvidenceNavigator
            ticketId={ticketId}
            attachments={(ticket.attachments ?? []).filter(({ name = '' }) =>
              name.toLowerCase().endsWith(csvType),
            )}
          />

          <DropdownMenu.Separator />
          <FollowUpButton ticket={ticket} />
          <TicketComments comments={ticket.comments ?? []} />
          <TicketAction ticket={ticket} />
        </VStack>
        <VStack
          padding="large"
          backgroundColor="widgetSecondary"
          marginTop="large"
          marginRight="large"
          marginBottom="large"
          yAlign="top"
          width="25%"
          gap="large"
          css={css`
            @media screen and (max-width: 1480px) {
              width: 100%;
              margin-top: 0;
              margin-bottom: 0;
              flex-wrap: wrap;
              flex-direction: row;
              order: 1;
            }
          `}
        >
          <Heading
            level="4"
            css={css`
              @media screen and (max-width: 1480px) {
                display: none;
              }
            `}
            paddingBottom="default"
          >
            {t('ticket:details.title')}
          </Heading>
          <DetailsTextRow
            title={t('ticket:details.ticketId')}
            value={ticketId}
          />
          <DetailsBadgeRow
            titleKey="ticket:details.status"
            value={ticket.status}
          />
          <VStack
            css={css({
              width: 'fit-content',
            })}
          >
            <Text styledAs="body4" whiteSpace="nowrap">
              {t('ticket:details.ticketType')}
            </Text>
            <TicketType value={ticket.type} />
          </VStack>
          <DetailsTextRow
            title={t('ticket:details.to')}
            value={
              <LogoBadge
                text={ticket.requester}
                showLogo={ticket.requesterAgent}
              />
            }
          />
          <DetailsTextRow
            title={t('ticket:details.cc')}
            value={
              ticket.ccAgent &&
              Boolean(ticket.ccAgent.length) && (
                <>
                  {ticket.ccAgent.map(({ isAgent, name }) => (
                    <LogoBadge key={name} text={name} showLogo={isAgent} />
                  ))}
                </>
              )
            }
          />
          <DetailsTextRow
            title={t('ticket:details.created')}
            value={<DateTime value={ticket.createdAt} />}
          />
          <DetailsTextRow
            title={t('ticket:details.lastUpdated')}
            value={<DateTime value={ticket.updatedAt} />}
          />
          <Attachments attachments={ticket.attachments} />
        </VStack>
      </HStack>
    </GeneralWrapper>
  );
};
